// @flow
import { type Node, Fragment } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { createHeader, Text } from '@tablekit/typography';
import { SPACING, typography, COLORS } from '@tablekit/theme';
import { MAX_WIDTH, MOBILE_GUTTER, Z_INDICES } from 'utils/theme';
import columnWidth from 'utils/columnWidth';
import RichText from 'components/RichText';
import StoryblokEditor from 'components/editable/StoryblokEditor';
import type { BlokComponentType, SimpleContentType } from './types';
import CTAButton from './CTAButton';
import Link from './Link';
import Image from './Image';

const curveHeight = 10;
const curveHeightDecimal = curveHeight / 100;
export const heroBannerId = 'hero-banner';

const sharedCss = ({ hasForm }) => css`
  color: ${COLORS.GRAY.L1};
  text-align: ${hasForm ? 'center' : 'left'};
  ${hasForm ? '' : 'align-self: flex-start;'};

  @media (min-width: 768px) {
    width: ${columnWidth(9)}%;
    // magicks...
    max-width: 720px;
  }
`;

const Banner = styled.div`
  display: flex;
  justify-content: ${({ hasForm }) => (hasForm ? 'center' : 'flex-start')};
  align-items: ${({ hasForm }) => (hasForm ? 'center' : 'flex-start')};
  flex-direction: column;
  position: relative;
  z-index: ${Z_INDICES.HERO_BANNER};

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    z-index: -3;
  }
  ${({ isCurved }) =>
    !isCurved
      ? ''
      : css`
          clip-path: url(#hero-bottom-border-mobile);
          @media (min-width: 768px) {
            clip-path: url(#hero-bottom-border-desktop);
          }
        `};
`;

const BannerBackground = styled(Image)`
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  height: 100%;
  transform: translate(-50%, -50%);
  z-index: -2;
  & > img {
    height: 100%;
    width: 100%;
    // only IE11 doesn't support this option
    // on IE11 some warping of the image will occur as it forces to above sizes
    object-fit: cover;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: ${({ hasForm }) => (hasForm ? 'center' : 'flex-start')};
  align-items: ${({ hasForm }) => (hasForm ? 'center' : 'flex-start')};
  flex: 1 1 auto;
  width: 100%;
  margin: ${parseInt(SPACING.L8, 10) + parseInt(SPACING.L6, 10)}px auto
    ${({ isCurved }) => (isCurved ? `${curveHeight / 2}%` : '0')};
  padding: 0 ${MOBILE_GUTTER};

  @media (min-width: 768px) {
    margin-top: ${({ hasForm }) =>
      hasForm
        ? parseInt(SPACING.L9, 10) + parseInt(SPACING.L10, 10)
        : parseInt(SPACING.L10, 10) + parseInt(SPACING.L6, 10)}px;
    max-width: ${MAX_WIDTH};
    padding: 0 ${SPACING.L7};
    ${({ isCurved }) =>
      isCurved
        ? css`
            margin-bottom: calc(20px + ${curveHeight / 2}%);
          `
        : '0'};
  }
`;

const Tint = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.57);
  z-index: -1;
  user-select: none;
`;

const Title = styled(
  createHeader('h1', { 'min-width: 768px': 'L10', default: 'L8' }),
  { shouldForwardProp: (prop) => ['hasForm'].indexOf(prop) === -1 }
)`
  ${sharedCss};
  margin-bottom: ${SPACING.L4};
  ${(props) => typography(props.hasForm ? 'L9' : 'L7')(props)};

  @media (min-width: 768px) {
    margin-bottom: ${SPACING.L2};
  }
`;

const Body = styled(Text.withComponent(RichText))`
  ${typography('L4')};
  ${sharedCss};
  white-space: pre-wrap;

  margin-bottom: ${({ hasForm }) => (hasForm ? SPACING.L8 : 0)};

  @media (min-width: 768px) {
    ${typography('L6')};
    margin-bottom: ${({ hasForm }) => (hasForm ? SPACING.L6 : SPACING.L4)};
  }
`;

const ButtonWrapper = styled.div`
  ${sharedCss};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  margin-bottom: ${SPACING.L8};
  width: 100%;

  @media (min-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
    width: auto;
  }
`;

const ModalButton = styled(CTAButton)`
  text-transform: uppercase;

  @media (min-width: 576px) {
    text-transform: none;
  }
`;

const nonCurvedPath = <path d="M 0 0 L 0 1 L 1 1 L 1 0 Z" />;

const backgroundImageOptions = [
  {
    breakpoint: '(min-width: 1280px)',
    options: {
      size: { width: 1440, height: 640 }
    }
  },
  {
    breakpoint: '(min-width: 1024px)',
    options: {
      size: { width: 1280, height: 640 }
    }
  },
  {
    breakpoint: '(min-width: 768px)',
    options: {
      size: { width: 1024, height: 640 }
    }
  },
  {
    breakpoint: '(min-width: 576px)',
    options: {
      size: { width: 768, height: 640 }
    }
  },
  {
    breakpoint: '(min-width: 360px)',
    options: {
      size: { width: 576, height: 604 }
    }
  },
  {
    options: {
      size: { width: 360, height: 425 }
    }
  }
];

const HeroBanner = ({
  blok,
  children = null,
  image: propsImage,
  isCurved = false
}: {
  blok?: ?BlokComponentType<SimpleContentType>,
  children?: Node,
  image?: string,
  isCurved?: boolean
}) => {
  const { image, body, link_text: linkText, link, title } = blok || {};
  return (
    <StoryblokEditor content={blok || {}}>
      <Banner id={heroBannerId} hasForm={!!linkText} isCurved={isCurved}>
        <svg width="0" height="0" viewBox="0 0 100 100">
          <defs>
            <clipPath
              id="hero-bottom-border-desktop"
              clipPathUnits="objectBoundingBox"
            >
              {isCurved ? (
                <path
                  d={`M 0 ${1 - curveHeightDecimal} q 0.5 ${
                    curveHeightDecimal * 2
                  } 1 0 L 1 -0.5 L 0 -0.5 Z`}
                />
              ) : (
                nonCurvedPath
              )}
            </clipPath>
            <clipPath
              id="hero-bottom-border-mobile"
              clipPathUnits="objectBoundingBox"
            >
              {isCurved ? (
                <path
                  d={`M 0 ${
                    1 - curveHeightDecimal / 2
                  } q 0.5 ${curveHeightDecimal} 1 0 L 1 -0.5 L 0 -0.5 Z`}
                />
              ) : (
                nonCurvedPath
              )}
            </clipPath>
          </defs>
        </svg>

        <Content hasForm={!!linkText} isCurved={isCurved}>
          {children || (
            <Fragment>
              <Title hasForm={!!linkText}>{title}</Title>
              <Body hasForm={!!linkText} richText={body} />
              {linkText && (
                <ButtonWrapper hasForm>
                  <ModalButton
                    type="button"
                    shouldFitContainer
                    component={Link}
                    gaReference="Hero Banner"
                    link={link}
                    size={{
                      'min-width: 768px': 'regular',
                      default: 'large'
                    }}
                  >
                    {linkText}
                  </ModalButton>
                </ButtonWrapper>
              )}
            </Fragment>
          )}
        </Content>
        <Tint />
        <BannerBackground
          src={propsImage || image}
          alt="Hero Banner"
          options={backgroundImageOptions}
        />
      </Banner>
    </StoryblokEditor>
  );
};

export default HeroBanner;
